/**
 * Hand-rolled .ics (iCalendar) generation and Google Calendar deep links for
 * the "Add to calendar" action on the order-confirmed screen. No calendar
 * library dependency — the format is small enough to build directly
 * (RFC 5545 VEVENT).
 *
 * Timezone note: the reservation instant is absolute. `reservationTime`
 * arrives from the backend as a UTC ISO string; `reservationBootstrap.ts`
 * keeps that raw value as `reservationAtIso` and renders the display strings
 * in Redux (`reservationDate`/`reservationTime`) from it in the
 * *restaurant's* timezone (`restaurant.timezone`). Display is owned upstream;
 * this module only needs the instant, which `resolveReservationStart` reads
 * from `reservationAtIso` (the display strings are a legacy fallback only).
 *
 * Because the instant is what matters, every DATE-TIME this module emits is
 * **UTC** (a `Z` suffix, RFC 5545 §3.3.5 form 2). A floating local value — no
 * `Z`, no `TZID` — is resolved by the *importing calendar's* own zone, which
 * is the Google account's configured timezone rather than the device that
 * built the link, so a guest whose calendar zone differs from the
 * restaurant's would get an event at the wrong absolute moment. UTC removes
 * that source of error; the calendar app then shows the event in whatever
 * zone the guest's calendar uses, at the correct moment.
 */

const DEFAULT_DURATION_HOURS = 2;

/** RFC 5545 §3.1: a content line is at most 75 octets before folding. */
const ICS_MAX_LINE_OCTETS = 75;

/**
 * Normalizes "5:00PM" / "5:00 pm" / "5:00 PM" into a form the native Date
 * parser reliably accepts ("5:00 PM"). `Date.parse` fails on a bare "PM"
 * glued to the minutes with no space.
 */
function normalizeAmPm(time: string): string {
  return time.trim().replace(/(\d)\s*([AaPp][Mm])$/, "$1 $2");
}

/**
 * Parses the app's already-localized `reservationDate` ("Friday, December
 * 27, 2025") and `reservationTime` ("5:00 PM") strings into a single Date.
 * Returns null if either input is missing or unparseable, so callers can
 * degrade gracefully (e.g. hide the calendar button) instead of downloading
 * a broken .ics file.
 */
export function parseReservationDateTime(
  reservationDate: string | undefined | null,
  reservationTime: string | undefined | null,
): Date | null {
  if (!reservationDate || !reservationTime) return null;
  const normalizedTime = normalizeAmPm(String(reservationTime));
  const parsed = new Date(`${reservationDate} ${normalizedTime}`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

/**
 * The reservation start instant, preferring the raw UTC ISO string the
 * backend sent (`reservation.reservationAtIso`) over the localized display
 * strings.
 *
 * The display strings are whatever `toLocaleDateString` produced for the
 * guest's *device locale*, and parsing a non-ISO string is
 * implementation-defined: a Spanish, Japanese, Chinese, Hindi or Arabic
 * rendering is `Invalid Date` in V8 (the calendar button disappeared), and an
 * engine that does parse one may land on a different instant. The ISO value
 * has none of that ambiguity. The string parse remains only as the fallback
 * for state persisted before that field existed.
 */
export function resolveReservationStart(
  reservationAtIso: string | undefined | null,
  reservationDate: string | undefined | null,
  reservationTime: string | undefined | null,
): Date | null {
  if (reservationAtIso) {
    const fromIso = new Date(reservationAtIso);
    if (!Number.isNaN(fromIso.getTime())) return fromIso;
  }
  return parseReservationDateTime(reservationDate, reservationTime);
}

/** Escapes text per RFC 5545 §3.3.11 (backslash, semicolon, comma, newline). */
export function escapeIcsText(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/** Formats a Date as a UTC DATE-TIME value: YYYYMMDDTHHMMSSZ */
function formatUtcDateTime(date: Date): string {
  return (
    `${date.getUTCFullYear()}${pad2(date.getUTCMonth() + 1)}${pad2(date.getUTCDate())}` +
    `T${pad2(date.getUTCHours())}${pad2(date.getUTCMinutes())}${pad2(date.getUTCSeconds())}Z`
  );
}

/**
 * Drops seconds from the event start. The display strings this module parses
 * carry hours and minutes only, so a reservation instant with a non-zero
 * seconds component would otherwise produce a different event depending on
 * which side of that round trip the caller got its Date from. Minute
 * granularity is also all any calendar UI shows.
 */
function startOfMinute(date: Date): Date {
  const truncated = new Date(date.getTime());
  truncated.setUTCSeconds(0, 0);
  return truncated;
}

const utf8 = new TextEncoder();

/**
 * Folds one content line to RFC 5545 §3.1: at most 75 **octets** per physical
 * line, continuations prefixed with a single space (which counts toward the
 * limit, hence 74 octets of content). Iterating code points rather than UTF-16
 * units keeps a multi-byte character — the em dash in "Aerlume — pre-order
 * placed" is 3 octets — from being sliced across a fold.
 */
function foldIcsLine(line: string): string {
  if (utf8.encode(line).length <= ICS_MAX_LINE_OCTETS) return line;

  const segments: string[] = [];
  let current = "";
  let currentOctets = 0;

  for (const char of line) {
    const charOctets = utf8.encode(char).length;
    const limit =
      segments.length === 0 ? ICS_MAX_LINE_OCTETS : ICS_MAX_LINE_OCTETS - 1;
    if (currentOctets + charOctets > limit) {
      segments.push(current);
      current = "";
      currentOctets = 0;
    }
    current += char;
    currentOctets += charOctets;
  }
  if (current) segments.push(current);

  return segments.join("\r\n ");
}

/**
 * Event body: the caller's description with the reservation deep link on its
 * own line beneath it. Either part may be absent.
 */
function composeDescription(
  description?: string,
  url?: string,
): string | undefined {
  const parts = [description, url].filter(
    (part): part is string => typeof part === "string" && part.trim() !== "",
  );
  return parts.length > 0 ? parts.join("\n") : undefined;
}

export interface IcsEventInput {
  /** The reservation instant. Emitted as UTC (see module note). */
  start: Date;
  /** Defaults to 2 hours. */
  durationHours?: number;
  summary: string;
  location?: string;
  description?: string;
  /** Deep link back to this reservation in the PWA. */
  url?: string;
  /** Defaults to a value derived from `start`. Pass explicitly for stable snapshots in tests. */
  uid?: string;
  /** Defaults to `new Date()`. Pass explicitly for stable snapshots in tests. */
  now?: Date;
}

/** Resolves an event input to its normalized start/end instants. */
function resolveEventWindow(input: IcsEventInput): { start: Date; end: Date } {
  const durationHours = input.durationHours ?? DEFAULT_DURATION_HOURS;
  const start = startOfMinute(input.start);
  const end = new Date(start.getTime() + durationHours * 60 * 60 * 1000);
  return { start, end };
}

/** Builds a complete .ics file (VCALENDAR + one VEVENT) as a string. */
export function buildIcsContent(input: IcsEventInput): string {
  const { start, end } = resolveEventWindow(input);
  const uid = input.uid ?? `preorder-${start.getTime()}@foyr.app`;
  const dtstamp = formatUtcDateTime(input.now ?? new Date());
  const description = composeDescription(input.description, input.url);

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Foyr//Pre-Order//EN",
    "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${dtstamp}`,
    `DTSTART:${formatUtcDateTime(start)}`,
    `DTEND:${formatUtcDateTime(end)}`,
    `SUMMARY:${escapeIcsText(input.summary)}`,
  ];
  if (input.location) {
    lines.push(`LOCATION:${escapeIcsText(input.location)}`);
  }
  if (description) {
    lines.push(`DESCRIPTION:${escapeIcsText(description)}`);
  }
  if (input.url) {
    // URL takes a URI value, and the deep link is percent-encoded by
    // `buildReservationDeepLink`, so escaping is a no-op for real input —
    // applied anyway so a stray separator can never break line parsing.
    lines.push(`URL:${escapeIcsText(input.url)}`);
  }
  lines.push("END:VEVENT", "END:VCALENDAR");

  // RFC 5545 uses CRLF line endings.
  return lines.map(foldIcsLine).join("\r\n");
}

/** Triggers a browser download of `content` as a named .ics file via a Blob link. */
export function downloadIcsFile(filename: string, content: string): void {
  const blob = new Blob([content], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * iOS detection incl. iPadOS 13+ (which reports as Macintosh but exposes
 * touch points). On iOS the platform-native path is an INLINE .ics served
 * by the backend: Safari hands text/calendar to Apple Calendar as an event
 * preview sheet — no file-download UI, user just taps Add.
 */
export function isIosDevice(
  nav: Pick<Navigator, "userAgent" | "maxTouchPoints"> = navigator,
): boolean {
  return (
    /iPad|iPhone|iPod/.test(nav.userAgent) ||
    (/Macintosh/.test(nav.userAgent) && (nav.maxTouchPoints ?? 0) > 1)
  );
}

/**
 * Google Calendar "render?action=TEMPLATE" deep link — opens the Google
 * Calendar app (Android) or web prefilled; the user only has to tap Save.
 * Times are UTC, matching this module's timezone note.
 */
export function buildGoogleCalendarUrl(input: IcsEventInput): string {
  const { start, end } = resolveEventWindow(input);
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: input.summary,
    dates: `${formatUtcDateTime(start)}/${formatUtcDateTime(end)}`,
  });
  if (input.location) params.set("location", input.location);
  const details = composeDescription(input.description, input.url);
  if (details) params.set("details", details);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Platform-split "Add to calendar":
 * - iOS → navigate to the backend's inline .ics endpoint (Apple Calendar
 *   event sheet opens directly).
 * - everything else → Google Calendar template URL in a new tab (prefilled
 *   event, user taps Save). Falls back to the .ics blob download only if
 *   the popup was blocked.
 */
export function openCalendarEvent(
  icsEndpointUrl: string,
  event: IcsEventInput,
): void {
  if (isIosDevice()) {
    window.location.assign(icsEndpointUrl);
    return;
  }
  const opened = window.open(
    buildGoogleCalendarUrl(event),
    "_blank",
    "noopener",
  );
  if (!opened) {
    downloadIcsFile("preorder-reservation.ics", buildIcsContent(event));
  }
}
